<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ElMessage, type UploadFile } from 'element-plus'
import { ref } from 'vue'

const props = withDefaults(defineProps<{ maxSizeMb?: number; acceptableMimeTypes?: any; disabled?: boolean }>(), {
  maxSizeMb: 10,
  acceptableMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
  disabled: false,
})

const model = defineModel<UploadFile | undefined | null>({ required: true })

const uploadRef = ref()

/**
 * Reads the uploaded file and sets the pdfData values with the file contents.
 * @param file the uploaded file
 */
const addFormFile = async (file: UploadFile) => {
  uploadRef.value?.clearFiles()

  if (!file.raw) return

  // Validate
  if (!props.acceptableMimeTypes.includes(file.raw.type)) {
    ElMessage.error('File must be a PDF, JPG, PNG or WEBP.')
    uploadRef.value?.clearFiles()
    return
  }
  if (file.raw.size > 1_048_576 * props.maxSizeMb) {
    ElMessage.error(`File size must be smaller than or equal to ${props.maxSizeMb}MB.`)
    uploadRef.value?.clearFiles()
    return
  }

  // Valid, so set the model the the uploaded file
  model.value = file
}

const clear = async () => {
  uploadRef.value?.clearFiles()
  model.value = null
}

defineExpose({ clear })
</script>

<template>
  <div class="w-full">
    <el-upload
      ref="uploadRef"
      class="w-full"
      :on-change="addFormFile"
      :on-remove="clear"
      :auto-upload="false"
      :accept="acceptableMimeTypes.join(', ')"
      drag
      :disabled="disabled"
    >
      <el-icon class="el-icon--upload">
        <icon icon="material-symbols:upload-rounded" :height="48" />
      </el-icon>

      <div>
        <slot />
      </div>
    </el-upload>
  </div>
</template>
